/* eslint-disable max-lines */
import {gql, useMutation} from "@apollo/client";
import AccountInfo, {QueryData as AccountInfoQueryData} from "../../components/account/AccountInfo";
import MembersFilterForm from "../../components/account/MembersFilterForm";
import MembersTableTable from "../../components/account/members-table/MembersTable.table";

import {
  Project,
  EducationMembersPaginatedList,
  Group,
  Challenge,
  ChallengeParticipationSummary,
  MembershipInput,
  MutationAddParticipantsArgs,
  MutationAddUsersArgs,
  MutationArchiveParticipationArgs,
  MutationImportMembersCsvArgs,
  MutationSetExtraEmailArgs,
  MutationSetUsersActivityArgs,
  MutationUpdateParticipationArgs,
  Participation,
  QueryGetAccountMembersArgs,
  Scalars,
  User,
  MutationSetReportsReceiveArgs,
} from "../../schema";

export type PickedChallenge = Pick<Challenge, "__typename" | "id" | "name">;
export type PickedProject = Pick<Project, "__typename" | "id" | "name"> & {
  challenges: Pick<Challenge, "id" | "name">[]
}

export type QueryData = AccountInfoQueryData & {
  users: User[] | undefined;
  educationItems: (PickedChallenge | PickedProject)[];
  flatChallenges: (PickedChallenge | PickedProject)[],
  groups: (Pick<Group, "id" | "name"> & {
    users: Pick<User, "id">[];
  })[];
  sessionsExportUrl: string
};

export type PaginatedListQueryVars = Omit<QueryGetAccountMembersArgs, "skip" | "first"> & {
  offset: QueryGetAccountMembersArgs["skip"];
  limit: QueryGetAccountMembersArgs["first"];
};

export type PaginatedListQueryData = {
  paginatedList: EducationMembersPaginatedList;
};

export type ParticipationsSummaryQueryData = {
  participationsSummary: ChallengeParticipationSummary;
};

export type UsersExportUrlWithFiltersQueryData = {
  usersExportUrlWithFilters: string
}

/* eslint-disable max-len */
const query = gql`
  query GetAccountMembersDataQuery($lang: String) {
    membersSummary: getAccountMembersSummary {
      __typename
      ...AccountInfoEducationMembersSummary
    }

    educationItems: getChallenges {
      __typename

      ... on Challenge {
        ...MembersTableChallenges
      }

      ... on Project {
        ...MembersTableProjects
        ...MembersFilterFormProjects
      }
    }

    flatChallenges: getChallenges (flatMode: true) {
      __typename

      ... on Challenge {
        ...MembersFilterFormChallenges
      }
    }

    contracts: getAccountContracts {
      __typename
      ...AccountInfoContract
    }

    account: getCurrentAccount {
      __typename
      id
      ...AccountInfoAccount
    }

    groups: getGroups {
      id
      name
      users {
        id
      }
      ...MembersFilterFormGroups
    }

    sessionsExportUrl: getAccountSessionsExportUrl(lang: $lang)
  }

  ${AccountInfo.fragments.membersSummary}
  ${MembersTableTable.fragments.projects}
  ${MembersTableTable.fragments.challenges}
  ${MembersFilterForm.fragments.projects}
  ${MembersFilterForm.fragments.challenges}
  ${MembersFilterForm.fragments.groups}
  ${AccountInfo.fragments.contract}
  ${AccountInfo.fragments.account}
`;

export type GetAllAccountMembersQueryData = {
  users: Array<User> | null;
};

export const getAllAccountMembersQuery = gql`
  query GetAllAccountMembersQuery {
    users: getCurrentAccountUsers {
      id
      fullName
      name
      email
      groups {
        id
        name
      }
    }
  }
`;

/* eslint-enable max-len */

export const paginatedListQuery = gql`
  query GetAccountMembersQuery(
    $limit: Int!
    $offset: Int
    $filter: EducationMemberFilterInput
    $sort: EducationMemberSortInput
  ) {
    paginatedList: getAccountMembers(first: $limit, skip: $offset, filter: $filter, sort: $sort) {
      items {
        id
        __typename

        ...MembersTable
      }

      countItems
    }
  }

  ${MembersTableTable.fragments.root}
`;

export const participationsSummaryQuery = gql`
  query GetParticipationsSummaryQuery($challengeId: ID, $projectId: ID, $groupsIds: [ID!]) {
    participationsSummary: getParticipationsSummary(challengeId: $challengeId, projectId:
     $projectId, groupsIds: $groupsIds) {
      __typename
      assignments
      started
      finished
      passed
    }
  }
`;

export const usersExportUrlWithFiltersQuery = gql`
  query GetUsersExportUrlWithFiltersQuery($filter: EducationMemberFilterInput) {
    usersExportUrlWithFilters: getAccountUsersExportUrl(filter: $filter)
  }
`;

export const usersExportUrlWithFiltersQueryXls = gql`
  query GetUsersExportUrlWithFiltersQuery($filter: EducationMemberFilterInput) {
    usersExportUrlWithFilters: getAccountUsersExportUrl(variant: "xls", filter: $filter)
  }
`;

export const reportsExportUrlsQuery = gql`
  query GetReportsExportUrlsQuery($challengeId: ID, $lang: String) {
    userBestAttemptsExportUrlTemplate: getUserBestAttemptsExportUrlTemplate

    participationBestAttemptReportUrlTemplate: getParticipationBestAttemptExportUrlTemplate

    challengeTranscriptsExportUrl: getChallengeTranscriptsExportUrl(challengeId: $challengeId, lang: $lang)

    challengeTranscriptsModifiedContentExportUrl: getChallengeTranscriptsExportUrl(
      challengeId: $challengeId
      lang: $lang
      modifiedContent: true
    )

    challengeTranscriptsExportUrlXls: getChallengeTranscriptsExportUrl(
      challengeId: $challengeId
      lang: $lang
      variant: "xls"
    )

    challengeTranscriptsModifiedContentExportUrlXls: getChallengeTranscriptsExportUrl(
      challengeId: $challengeId
      lang: $lang
      variant: "xls"
      modifiedContent: true
    )
  }
`;

export function useUpdateMembershipMutation() {
  return useMutation<
    {
      user: User;
    },
    {
      data: MembershipInput;
      userId: Scalars["ID"];
    }
  >(updateMembershipMutation);
}

export function useUpdateParticipationMutation() {
  return useMutation<
    {
      participation: Participation;
    },
    MutationUpdateParticipationArgs
  >(updateParticipationMutation);
}

export function useArchiveParticipationMutation() {
  return useMutation<
    {
      participation: Participation;
    },
    MutationArchiveParticipationArgs
  >(archiveParticipationMutation);
}

export function useSetReportsReceiveMutation() {
  return useMutation<{success: boolean}, MutationSetReportsReceiveArgs>(setReportsReceiveMutation);
}

const setReportsReceiveMutation = gql`
  mutation SetReportsReceiveMutation($status: Boolean!) {
    success: setReportsReceive(status: $status)
  }
`;

export function useSetExtraEmailMutation() {
  return useMutation<{success: boolean}, MutationSetExtraEmailArgs>(setExtraEmailMutation);
}

const setExtraEmailMutation = gql`
  mutation SetExtraEmailMutation($email: String!) {
    success: setExtraEmail(email: $email)
  }
`;

const updateMembershipMutation = gql`
  mutation UpdateMembershipMutation($userId: ID!, $data: MembershipInput!) {
    user: updateMembership(userId: $userId, data: $data) {
      id
      hasActiveMembership
    }
  }
`;

const updateParticipationMutation = gql`
  mutation UpdateParticipationMutation(
    $userId: ID
    $emails: [String!]
    $status: Boolean!
    $challengeId: ID
    $projectId: ID
    $deactivateAt: DateTimeTimestamp
  ) {
    participations: updateParticipation(
      userId: $userId
      emails: $emails
      challengeId: $challengeId
      projectId: $projectId
      status: $status
      deactivateAt: $deactivateAt
    ) {
      id
      isActive
      deactivatedAt
    }
  }
`;

const archiveParticipationMutation = gql`
  mutation ArchiveParticipationMutation($userId: ID, $emails: [String!], $challengeId: ID, $projectId: ID) {
    participations: archiveParticipation(
      userId: $userId
      emails: $emails
      challengeId: $challengeId
      projectId: $projectId
    ) {
      id
    }
  }
`;

export function useAddUsersMutation() {
  return useMutation<
    {
      users: User[];
    },
    MutationAddUsersArgs
  >(addUsersMutation);
}

export function useSetUsersActivityMutation() {
  return useMutation<
    {
      users: User[];
    },
    MutationSetUsersActivityArgs
  >(setUsersActivityMutation);
}

export function useInviteParticipantsMutation() {
  return useMutation<
    {
      participations: Participation[];
    },
    MutationAddParticipantsArgs
  >(inviteParticipantsMutation);
}

export function useImportMembersCsvMutation() {
  return useMutation<{result: string}, MutationImportMembersCsvArgs>(importMembersCsvMutation);
}

const inviteParticipantsMutation = gql`
  mutation InviteParticipantsMutation(
    $challenges: [ChallengeParticipantsType!]
    $usersIds: [ID!]
    $emails: [String!]
    $adminText: String
  ) {
    participations: addParticipants(
      usersIds: $usersIds
      challenges: $challenges
      emails: $emails
      adminText: $adminText
    ) {
      id
      student {
        id
        name
        fullName
        email
      }
    }
  }
`;

const addUsersMutation = gql`
  mutation AddUsersMutation($emails: [String!]!, $names: [String!]!, $languageCode: LanguageCode) {
    members: addUsers(emails: $emails, names: $names, languageCode: $languageCode) {
      id
      name
      fullName
      email
    }
  }
`;

const setUsersActivityMutation = gql`
  mutation SetUsersActivityMutation($emails: [String!]!, $isActive: Boolean!) {
    members: setUsersActivity(emails: $emails, isActive: $isActive) {
      id
      name
      fullName
      email
      hasActiveMembership
    }
  }
`;

const importMembersCsvMutation = gql`
  mutation ImportMembersCsvMutation(
    $attachment: Upload!
    $sendWelcomeEmails: Boolean!
    $overrideActive: Boolean!
    $emailLanguage: LanguageCode!
  ) {
    result: importMembersCsv(
      attachment: $attachment
      sendWelcomeEmails: $sendWelcomeEmails
      overrideActive: $overrideActive
      emailLanguage: $emailLanguage
    )
  }
`;

export default query;
